import { ReactElement } from 'react';
import classNames from 'classnames';

import { icon_arrow_right } from '@gn-audio/icons';

import { mapSharedPropertiesToFields } from '../../../getters';
import { SitecoreFieldTypes } from '../../../types';
import useFallbackTranslation from '../../../utils/hooks/useFallbackTranslation';
import { Button } from '../../Button/Button';
import { JabraFocusItem } from '../../JabraFocus';
import { JabraIcon } from '../../JabraIcon/JabraIcon';
import { ScopedStyle } from '../../ScopedStyle/ScopedStyle';
import { MajorLinkBigCard } from '../MegaMenuTypes';
import { getBgBasedColor, getRandomBGColor } from '../MegaMenuUtils';
import { BackgroundImage } from './BackgroundImage';

import styles from './Cards.module.css';

interface BigCardProps {
  data: {
    id: string;
    fields: SitecoreFieldTypes;
  };
}

export const BigCard = ({ data }: BigCardProps): ReactElement | null => {
  const { t } = useFallbackTranslation();

  const cardSettings = mapSharedPropertiesToFields<MajorLinkBigCard>(data.fields);

  const isBackgroundImageSet =
    !!cardSettings.BackgroundImage?.value?.src ||
    !!cardSettings.backgroundImageExternalLink?.value?.href;

  const bgColor = isBackgroundImageSet ? 'big-photo' : getRandomBGColor(data.id);
  const bgBasedColor = getBgBasedColor(bgColor);

  return (
    <>
      {!!cardSettings.Link?.value && (
        <ScopedStyle
          id={data.id}
          css={cardSettings.Style?.value || ''}
          renderItem={(styleLinkingProp) => (
            <JabraFocusItem asChild>
              <a
                href={cardSettings.Link?.value?.href}
                className={classNames(
                  'group relative col-span-1 min-h-[460px] hover:no-underline',
                  `${styles.card} ${styles.bigCard} ${bgColor} ${bgBasedColor.text}`
                )}
                {...styleLinkingProp}
              >
                <div className="absolute inset-0 z-10 bg-gradient-to-t from-[rgba(0,0,0,0.5)] via-[rgba(0,0,0,0.5)] to-60%" />
                {isBackgroundImageSet && (
                  <BackgroundImage
                    field={cardSettings.BackgroundImage}
                    externalField={{
                      src: cardSettings.backgroundImageExternalLink?.value?.href,
                      altText: cardSettings.Header?.value,
                    }}
                    cardType="big"
                  />
                )}
                <div className="relative z-20 w-full">
                  <div className="absolute -bottom-[1.5rem] -left-[1.5rem] h-[calc(100%+3rem)] w-[calc(100%+3rem)] -translate-x-full rounded-r-full bg-blue-800 opacity-90 transition-transform duration-300 group-hover:translate-x-0" />
                  <div className="relative flex-col">
                    <p className={`${styles.header}`}>{cardSettings.Header?.value}</p>
                    <p
                      className={`${styles.description}`}
                      dangerouslySetInnerHTML={{ __html: cardSettings.Description?.value }}
                    />
                    <Button
                      variant="primary"
                      size="m"
                      aria-label={t('topmenu.ArrowRight')}
                      icon={<JabraIcon className="size-5" Icon={icon_arrow_right} />}
                      noKeyboard
                    />
                  </div>
                </div>
              </a>
            </JabraFocusItem>
          )}
        />
      )}
    </>
  );
};
